import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gift } from "lucide-react";
import { RevealTextOnView } from "../components/RevealText";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

export default function ChiSono() {
  const photoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: photoRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "15%"]);

  return (
    <div className="pt-32 md:pt-40 pb-16 px-6 md:px-10" data-testid="chi-sono-page">
      <div className="max-w-[1600px] mx-auto">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">— Chi sono</p>
        <h1 className="font-display text-[14vw] md:text-[9vw] lg:text-[11rem] leading-[0.85] text-[#3D2140] mb-16">
          <RevealText text={["Presente,", "con cura."]} />
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5 md:sticky md:top-32 self-start">
            <div ref={photoRef} className="relative aspect-[4/5] overflow-hidden rounded-[40px] border-4 border-white shadow-2xl">
              <motion.img
                src={BRAND.photoChiSono}
                alt="Teresa Vilardo"
                style={{ y }}
                className="w-full h-[115%] object-cover"
              />
            </div>
            <img
              src={BRAND.logo}
              alt=""
              aria-hidden
              className="absolute md:relative -bottom-6 -right-6 md:mt-6 md:ml-auto h-28 w-28 rounded-full animate-floaty drop-shadow-xl"
            />
          </div>

          <div className="md:col-span-7 space-y-8 text-lg leading-relaxed text-[#3D2140]/85">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-editorial text-3xl md:text-4xl text-[#3D2140] leading-snug"
            >
              Sono Teresa Vilardo, <em>Educatrice nei servizi per l'infanzia</em>, <em>Family Coach Montessori</em>, <em>Educatrice neonatale</em> e <em>Consulente in Puericultura 0–36 mesi</em>. In oltre 23 anni accanto ai bambini e alle loro famiglie ho maturato il <em>Metodo T.A.T.A.</em>, la mia visione educativa relazionale.
            </motion.p>

            <p>Credo che ogni bambino abbia un ritmo suo — nel sonno, nell'alimentazione, nel gioco, nel crescere. Il mio lavoro è aiutarti a riconoscerlo, senza forzare tappe né inseguire mode. Con rispetto, presenza e leggerezza.</p>

            <p>Mi occupo di consulenze personalizzate su sonno, avvicinamento all'alimentazione complementare, abbandono dolce del ciuccio e del pannolino, giochi e Metodo Montessori. Conduco incontri di ginnastica neonatale, psicomotricità e massaggio infantile. Organizzo feste ed eventi curati per bambine e bambini. Propongo gli stessi temi anche alle tate, in corsi one to one dedicati — all'interno di <strong>T.A.T.A. Academy</strong>.</p>

            <div className="grid grid-cols-2 gap-6 py-8 border-y border-[#81C3C9]/40">
              {[
                { n: "23+", l: "Anni di esperienza" },
                { n: "500+", l: "Famiglie accompagnate" },
                { n: "100%", l: "Riservatezza" },
                { n: "1:1", l: "Approccio personale" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-display text-5xl md:text-6xl text-[#FF2768] leading-none mb-2">{s.n}</p>
                  <p className="text-xs uppercase tracking-widest text-[#3D2140]/60">{s.l}</p>
                </div>
              ))}
            </div>

            <p>Se sei un genitore che cerca ascolto e strumenti concreti, o una tata che vuole crescere nella propria professione, sono qui per accompagnarti. Con la stessa cura che riservo a ogni bambino che incontro.</p>

            <div className="pt-4 flex flex-wrap gap-3">
              <Link to="/prenota" className="btn-tv-primary" data-testid="chisono-cta-booking">
                Prenota una consulenza <ArrowRight size={16} />
              </Link>
              <Link to="/cosa-posso-fare-per-te" className="btn-tv-outline" data-testid="chisono-cta-services">
                Cosa posso fare per te
              </Link>
              <Link
                to="/voucher-regalo"
                data-testid="chisono-cta-regali"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold uppercase tracking-wider bg-transparent text-[#3D2140] border border-dashed border-[#FF2768]/60 hover:border-solid hover:bg-[#FF2768] hover:text-white hover:border-[#FF2768] transition-all"
              >
                <Gift size={16} /> Regali di Cura
              </Link>
            </div>
          </div>
        </div>

        {/* CERTIFICAZIONI & FORMAZIONE */}
        <div className="mt-24 md:mt-32 max-w-[1500px] mx-auto" data-testid="chisono-certifications">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
            <div className="md:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Formazione & Titoli</p>
              <h2 className="font-display text-4xl md:text-6xl text-[#3D2140] leading-[0.95] mb-4">
                Un percorso di studi<br/><span className="text-[#81C3C9]">al fianco della cura.</span>
              </h2>
              <p className="text-[#3D2140]/70 leading-relaxed">
                La mia formazione professionale si intreccia con l'esperienza sul campo: ogni titolo è uno strumento, ogni corso una porta aperta sui bisogni delle famiglie.
              </p>
            </div>
            <div className="md:col-span-7">
              <ul className="space-y-4">
                {[
                  {
                    title: "Educatrice nei servizi per l'infanzia",
                    sub: "Laurea in Scienze dell'Educazione",
                  },
                  {
                    title: "Family Coach Montessori",
                    sub: "Formazione specialistica sull'approccio Montessori",
                  },
                  {
                    title: "Educatrice neonatale",
                    sub: "Specializzazione nella cura del neonato 0-6 mesi",
                  },
                  {
                    title: "Operatrice in Psicomotricità",
                    sub: "Formazione psicomotoria educativa",
                  },
                  {
                    title: "Consulente in Puericultura",
                    sub: "Percorsi di sonno, alimentazione, autonomie",
                  },
                  {
                    title: "Consulente del sonno e addormentamento dei più piccoli",
                    sub: "Accompagnamento dolce nella regolazione del sonno",
                  },
                  {
                    title: "Consulente in alimentazione complementare",
                    sub: "Introduzione ai cibi solidi e svezzamento",
                  },
                  {
                    title: "Consulente per l'abbandono dolce del ciuccio",
                    sub: "Percorsi graduali e rispettosi",
                  },
                  {
                    title: "Consulente per l'abbandono graduale e rispettoso del pannolino",
                    sub: "Spannolinamento senza forzature",
                  },
                  {
                    title: "Operatrice del tocco e massaggio infantile",
                    sub: "Contatto relazionale e benessere del neonato",
                  },
                  {
                    title: "Formazione continua",
                    sub: "Aggiornamento costante su metodo, pedagogia e prima infanzia",
                  },
                ].map((c, i) => (
                  <li key={i} className="bg-white rounded-2xl p-5 md:p-6 border border-[#3D2140]/8 flex items-start gap-4">
                    <div className="h-10 w-10 rounded-xl bg-[#FF2768]/10 flex items-center justify-center flex-shrink-0">
                      <span className="font-display text-lg text-[#FF2768]">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-[#3D2140] leading-tight">{c.title}</p>
                      <p className="text-sm text-[#3D2140]/60 mt-0.5">{c.sub}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ZONE DI INTERVENTO */}
        <div className="mt-24 md:mt-32 max-w-[1500px] mx-auto" data-testid="chisono-zones">
          <div className="rounded-3xl bg-[#3D2140] text-white p-8 md:p-14 lg:p-16 relative overflow-hidden">
            <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-[#FF2768]/25 blur-3xl pointer-events-none" />
            <div className="absolute -left-12 -bottom-12 h-64 w-64 rounded-full bg-[#81C3C9]/20 blur-3xl pointer-events-none" />

            <div className="relative">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Dove lavoro</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] mb-6">
                Vengo dove<br/><span className="text-[#81C3C9]">c'è bisogno.</span>
              </h2>
              <p className="text-white/80 max-w-2xl leading-relaxed mb-10">
                Il mio lavoro nasce dal contatto diretto con le famiglie. Opero abitualmente nelle zone qui sotto, ma sono aperta a valutare insieme la vostra situazione — <em>previo contatto e conferma reciproca</em>.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    icon: "📍",
                    label: "Zona base",
                    value: "Bologna e provincia",
                    sub: "Presenza abituale a domicilio",
                  },
                  {
                    icon: "🚗",
                    label: "Trasferte",
                    value: "Emilia-Romagna e oltre",
                    sub: "Weekend, giornaliere, notti — previo contatto",
                  },
                  {
                    icon: "💻",
                    label: "Online",
                    value: "In tutta Italia",
                    sub: "Consulenze via videocall personalizzate",
                  },
                ].map((z, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur">
                    <span className="text-3xl">{z.icon}</span>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] mt-4 mb-2 font-semibold">
                      {z.label}
                    </p>
                    <p className="font-display text-2xl md:text-3xl mb-1">{z.value}</p>
                    <p className="text-sm text-white/60">{z.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
